import { Component, OnInit } from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { ToastrService } from 'ngx-toastr';
import { AiImageService } from 'src/app/shared/services/ai-image.service';
import { MediaUploadService } from 'src/app/shared/services';
import { HttpEventType } from '@angular/common/http';
import { AccountSetupHelperService } from '../../account-setup-helper.service';
import { AiFieldQueueService } from 'src/app/shared/services/ai-field-queue.service';
import { AiService } from 'src/app/shared/services/ai.service';
import { lastValueFrom } from 'rxjs';

interface StyleOption {
  id: string;
  name: string;
  description: string;
}

interface GenerationHistoryItem {
  url: string;
  fileId: number[];
  prompt: string;
  style: string;
  timestamp: Date;
}

@Component({
  selector: 'app-hub-home-banner-step',
  templateUrl: './hub-home-banner-step.component.html',
  styleUrls: ['./hub-home-banner-step.component.css'],
  animations: [
    trigger('imageReveal', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scale(0.95)',
        }),
        animate(
          '600ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({
            opacity: 1,
            transform: 'scale(1)',
          }),
        ),
      ]),
    ]),
  ],
})
export class HubHomeBannerStepComponent implements OnInit {
  bannerMode: 'upload' | 'ai-generate' = 'ai-generate';
  bannerPrompt = '';
  showPreviewSection = false;
  isGeneratingPrompt = false;
  isGenerating = false;
  selectedBanner: string | null = null;
  generationCount = 0;
  maxGenerations = 5;

  // History management
  generationHistory: GenerationHistoryItem[] = [];
  currentHistoryIndex = -1;

  defaultBannerPrompt = `Create a modern hero section banner for a website homepage. It should feature a bold, creative layout with a background image showing collaboration, innovation, or technology in a clean, inspiring tone. Aspect ratio: 16:9.`;

  isDropdownOpen = false;

  selectedStyle: string;
  STYLE_PROMPTS: Record<string, string> = {
    realistic: `Create a wide image showing happy children aged 7–12 in a bright, modern classroom, using laptops and tablets with colorful coding blocks on the screens. The scene should feel natural and inspiring, with kids collaborating and smiling. Natural light, vibrant but realistic colors. Background should be classroom-like but soft-focused.`,
    illustration: `Create a wide image in flat vector illustration style, showing smiling kids assembling colorful coding blocks on screens. Surround them with floating playful coding icons like logic blocks, puzzle pieces, arrows, and small characters. Use vibrant colors and clean, rounded lines to emphasize coding as fun and visual.`,
    '3d': `Create a wide 3D image where children interact with floating 3D coding blocks shaped like puzzle pieces. Blocks snap together in the air, forming rockets, animals, or simple animations. Bright, smooth shading with rounded edges. The environment should feel futuristic yet kid-friendly, with coding front and center.`,
    futuristic: `Create a wide image where children use holographic coding interfaces. Glowing neon coding blocks float mid-air, snapping together to create virtual robots and rockets. The background should feel like a futuristic digital playground, with neon lines and holographic effects. Coding should look magical but still understandable.`,
    creative: `Create a wide image where a magical glowing laptop or tablet projects colorful coding blocks into the air. The blocks float upward and transform into playful creatures, rockets, or mini-games. Children reach out joyfully to interact with the floating code. Add sparkles, glowing trails, and a whimsical color palette.`,
    minimalist: `Create a wide image with a clean, modern design. Children are shown simply dragging colorful coding blocks on tablets. Around them, a few puzzle-piece coding icons float subtly in pastel colors. Lots of whitespace, soft gradients, and a calm but kid-friendly aesthetic. Focus on coding blocks as the core element.`,
  };

  STYLES: StyleOption[] = [
    {
      id: 'realistic',
      name: 'Realistic',
      description: 'Professional, photo-like, serious and academic',
    },
    {
      id: 'illustration',
      name: 'Illustration',
      description: 'Fun, flat/vector-style, clean lines, bright colors',
    },
    {
      id: '3d',
      name: '3D',
      description: 'Bold, modern, depth, isometric elements',
    },
    {
      id: 'futuristic',
      name: 'Futuristic / Sci-Fi',
      description: 'Neon lights, holograms, cyberpunk energy',
    },
    {
      id: 'creative',
      name: 'Creative / Magical',
      description: 'Imaginative, glowing effects, fantasy elements',
    },
    {
      id: 'minimalist',
      name: 'Minimalist / Professional',
      description: 'Clean, elegant, gradients, typography',
    },
  ];

  constructor(
    private aiImageService: AiImageService,
    private mediaUpload: MediaUploadService,
    private toastr: ToastrService,
    public accountSetupHelperService: AccountSetupHelperService,
    public aiService: AiService,
    public aiQueue: AiFieldQueueService,
  ) {
    this.currentHistoryIndex =
      this.accountSetupHelperService.wizardData.hubInput.hubHomePageBanner.currentHistoryIndex;
    this.generationHistory =
      this.accountSetupHelperService.wizardData.hubInput.hubHomePageBanner.generationHistory;
    if (
      this.currentHistoryIndex >= 0 &&
      this.currentHistoryIndex < this.generationHistory.length
    ) {
      this.applyHistoryItem(this.generationHistory[this.currentHistoryIndex]);
    }
  }

  ngOnInit(): void {
    if (!this.selectedStyle) {
      this.onStyleChange(this.STYLES[0].id);
    }
  }

  promptOfPrompt(style: StyleOption): string {
    return `
    You are an expert AI art prompt engineer who creates vivid and visually appealing image prompts for AI image generation tools.
    
    Your task is to create a single, detailed image prompt that reflects the concept, audience, and mood described below — in the selected visual style.
    
    Here's an example for reference:
    "I need an image for my course 'CodePlay: Fun Coding for Kids with Visual Programming'. A playful, modern illustration of kids happily dragging colorful coding blocks on a laptop/tablet, with vibrant shapes, icons (puzzle pieces, rockets, robots), and a bright fun background. Style: flat illustration, vector, digital art. Aspect ratio: 16:9. Mood: cheerful, inspiring, fun, educational."
    
    ---
    🧩 Input Data:
    - Idea / Purpose: "${this.accountSetupHelperService.wizardData.hubInput.aiState.aiData.idea}" 
    - Target Audience: "${this.accountSetupHelperService.wizardData.hubInput.aiState.aiData.audience}" 
    - Main Goals or Focus: "${this.accountSetupHelperService.wizardData.hubInput.aiState.aiData.goals}" 
    - Visual Style: "${style?.name}" — ${style?.description}
    - Base Style Prompt: "${this.STYLE_PROMPTS[style?.id]}"
    ---
    
    🎨 Your Task:
    - Combine all the above data into one **concise, vivid, image generation prompt**.
    - You **must integrate** the essence of the selected visual style.
    - Use descriptive language focusing on **scene composition, emotion, and environment**.
    - Always return a **single paragraph** with no extra text, labels, or formatting.
    - End the prompt naturally (no quotes, no "prompt:" prefix).
    
    Return only the generated image prompt.
    `;
  }

  get selectedStyleName(): string {
    const found = this.STYLES.find((s) => s.id === this.selectedStyle);
    return found ? found.name : 'Select tone or style';
  }

  get selectedStyleDescription(): string {
    const found = this.STYLES.find((s) => s.id === this.selectedStyle);
    return found ? found.description : '';
  }

  onStyleChange(styleId: string) {
    this.selectedStyle = styleId;
    const style = this.STYLES.find((s) => s.id === this.selectedStyle);
    this.generateBannerPrompt(style);
  }

  setBannerMode(mode: 'upload' | 'ai-generate') {
    this.bannerMode = mode;
  }

  onImageSelected(fileIds: any) {
    this.selectedBanner = null;
    this.toastr.success('Banner uploaded successfully!');
  }

  async generateBannerPrompt(style: StyleOption): Promise<void> {
    if (this.isGeneratingPrompt) return;

    this.isGeneratingPrompt = true;
    try {
      await this.aiQueue.enqueue('', async () => {
        const generatedText = await lastValueFrom(
          this.aiService.getChatCompletion([], this.promptOfPrompt(style), {
            stream: false,
            model: this.aiService.getCurrentModel(),
          }),
        );

        this.bannerPrompt = generatedText;
      });
    } catch (error) {
      console.error('AI generation failed:', error);
    } finally {
      this.isGeneratingPrompt = false;
    }
  }

  scrollToPreviewSection() {
    const previewSection = document.getElementById('previewSection');
    if (previewSection) {
      previewSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  handleBannerGenerate() {
    // this.scrollToPreviewSection();
    if (this.generationCount >= this.maxGenerations) {
      this.toastr.error('Generation limit reached.', 'Limit');
      return;
    }

    this.isGenerating = true;

    this.aiImageService
      .generateImageHF({
        prompt: this.bannerPrompt || this.defaultBannerPrompt,
        steps: 20,
        width: 1664,
        height: 928,
      })
      .subscribe({
        next: (image) => {
          this.uploadBanner(image);
        },
        error: () => {
          this.isGenerating = false;
          this.toastr.error('Failed to generate banner.');
        },
      });
  }

  uploadBanner(base64Image: string) {
    const file = this.mediaUpload.base64ToFile(base64Image, 'banner.png');

    this.mediaUpload.uploadFile(file, 'media').subscribe({
      next: (event) => {
        if (event.type === HttpEventType.Response) {
          const uploadedUrl = event.body.uploadedFiles[0].url;
          const uploadedIds = event.body.uploadedFiles.map((file) => file.id);

          // Add to history
          this.addToHistory({
            url: uploadedUrl,
            fileId: uploadedIds,
            prompt: this.bannerPrompt || this.defaultBannerPrompt,
            style: this.selectedStyle,
            timestamp: new Date(),
          });

          this.selectedBanner = uploadedUrl;
          this.isGenerating = false;
          this.generationCount++;
          this.toastr.success('AI banner generated successfully!');
          this.accountSetupHelperService.wizardData.hubInput.hubHomePageBanner.image_id =
            uploadedIds;
        }
      },
      error: () => {
        this.isGenerating = false;
        this.toastr.error('Upload failed.');
      },
    });
  }

  // History Management Methods
  addToHistory(item: GenerationHistoryItem) {
    // Remove any items after current index (if user went back and generated new)
    if (this.currentHistoryIndex < this.generationHistory.length - 1) {
      this.generationHistory = this.generationHistory.slice(
        0,
        this.currentHistoryIndex + 1,
      );
    }

    // Add new item
    this.generationHistory.push(item);
    this.currentHistoryIndex = this.generationHistory.length - 1;

    // Limit history to last 10 items to prevent memory issues
    if (this.generationHistory.length > 10) {
      this.generationHistory.shift();
      this.currentHistoryIndex--;
    }

    this.accountSetupHelperService.wizardData.hubInput.hubHomePageBanner.currentHistoryIndex =
      this.currentHistoryIndex;
    this.accountSetupHelperService.wizardData.hubInput.hubHomePageBanner.generationHistory =
      this.generationHistory;
  }

  navigateHistory(direction: 'back' | 'forward') {
    if (direction === 'back' && this.currentHistoryIndex > 0) {
      this.currentHistoryIndex--;
      this.applyHistoryItem(this.generationHistory[this.currentHistoryIndex]);
    } else if (
      direction === 'forward' &&
      this.currentHistoryIndex < this.generationHistory.length - 1
    ) {
      this.currentHistoryIndex++;
      this.applyHistoryItem(this.generationHistory[this.currentHistoryIndex]);
    }
  }

  selectFromHistory(index: number) {
    if (index >= 0 && index < this.generationHistory.length) {
      this.currentHistoryIndex = index;
      this.applyHistoryItem(this.generationHistory[index]);
    }
  }

  private applyHistoryItem(item: GenerationHistoryItem) {
    this.selectedBanner = item.url;
    this.bannerPrompt = item.prompt;
    this.selectedStyle = item.style;
    this.accountSetupHelperService.wizardData.hubInput.hubHomePageBanner.image_id =
      item.fileId;
  }
}
